import { useEffect, useMemo, useRef, useState } from "react";

import { AbsoluteResolvedModelPath, PathTokens } from "../../../../../Common/ModelPath/ModelPath";
import { FilterExpression } from "../../../Helpers/FilterExpressions/FilterExpression";
import { useFormObject } from "../../../Controls/FormObjectProvider";
import { useAsyncEffect } from "../../../../../Common/UseAsyncEffect";
import { wrapDecimalOrUndefined } from "../../../Helpers/AutocalcCommonFunctions";
import { delay } from "../../../../../Common/AsyncUtils";
import { isAbortException } from "../../../../../Common/Errors";
import { getAllPathsFromExpression } from "../../../Controls/MultiControls/MultiLine/MultiLineFilter";
import { resolveFilterDataPath } from "../../../Helpers/FilteringHelpers";
import { isNotNullOrUndefined, reject } from "../../../../../Common/TypingUtils";
import { AbsoluteModelPathSet } from "../../../../../Common/ModelPath/Set/AbsoluteModelPathSet";
import { IterUtils } from "../../../../../Common/IterableUtils";
import { useTypedSelector } from "../../../../../Common/UseTypedSelector";

import { useMultilineClient } from "./UseMultilineClient";
import { AggregationType, SelectAggregationClause, SelectAggregationClauseResult } from "./IAggregationQueryStrategy";
import { ServerSideAggregationQueryStrategy } from "./ServerSideAggregationQueryStrategy";

const delayBetweenRequestsMs = 2000;

export type SelectAggregationClauseWithFilter = SelectAggregationClause & { filter?: FilterExpression };

export interface IAggregationQueryProps {
    multiplicityPath: AbsoluteResolvedModelPath;
    includeMultilineFilters: boolean;
    uniqueTableId: string;
    filter?: FilterExpression;
    selects: SelectAggregationClauseWithFilter[];
}

export interface IAggregationQueryResult {
    isLoading: boolean;
    isStale: boolean;
    error: Error | undefined;
    result: SelectAggregationClauseResult[];
}

export function useAggregationQuery(props: IAggregationQueryProps): IAggregationQueryResult {
    const [result, setResult] = useState<IAggregationQueryResult>({
        isLoading: false,
        isStale: true,
        error: undefined,
        result: props.selects.map(_ => undefined),
    });
    const { filters, isMultilineConnected } = useMultilineClient(props.multiplicityPath, props.uniqueTableId, x => ({
        filters: x.multilineState.filters,
        isMultilineConnected: x.multilineState.isMultilineConnected,
        useServerFilter: x.multilineState.useServerFilter,
    }));

    const isFormReloading = useTypedSelector(state => state.formVisualState.isFormReloading);

    const form = useFormObject();
    const aggregationStrategy = useMemo(() => {
        const strategy = new ServerSideAggregationQueryStrategy(form.candyApiDraftClient);
        return strategy;
    }, [form.candyApiDraftClient]);

    const awaiterBeforeNextRequest = useRef(Promise.resolve());

    const [changesVersion, setChangesVersion] = useState(0);

    useEffect(() => {
        const selectsPaths = props.selects
            .filter(x => x.type == AggregationType.Sum)
            .map(x => props.multiplicityPath.joinWith(PathTokens.each).joinWith(x.relativeFieldPath));

        const paths = (props.includeMultilineFilters ? filters : [])
            .flatMap(x => x.expression as FilterExpression | undefined)
            .concat(props.filter)
            .filter(isNotNullOrUndefined)
            .flatMap(x => getAllPathsFromExpression(x))
            .map(x => resolveFilterDataPath(x, props.multiplicityPath).path);

        const allPathsSet = new AbsoluteModelPathSet();
        for (const path of paths.concat(selectsPaths)) {
            allPathsSet.add(path);
        }

        const subscription = form.onModelChange(
            [...allPathsSet.values()].map(x => x.toLegacyPath()),
            () => {
                setChangesVersion(prev => (Number.isFinite(prev) ? prev : 0) + 1);
            }
        );

        return () => {
            subscription.unsubscribe();
        };
    }, [props.includeMultilineFilters ? filters : null, props.filter, props.selects]);

    useAsyncEffect(
        async abortSignal => {
            if (isFormReloading) {
                return;
            }

            const extraFilters = props.filter != undefined ? [props.filter] : [];
            try {
                setResult(prevState => ({
                    isLoading: false,
                    isStale: true,
                    error: undefined,
                    result: prevState.result,
                }));

                if (props.includeMultilineFilters && !isMultilineConnected) {
                    return;
                }

                await awaiterBeforeNextRequest.current;
                await form.changeSet.send();
                awaiterBeforeNextRequest.current = delay(delayBetweenRequestsMs);
                abortSignal.throwIfAborted();

                setResult(prevState => ({
                    isLoading: true,
                    isStale: true,
                    error: undefined,
                    result: prevState.result,
                }));

                const baseFilters = props.includeMultilineFilters
                    ? filters.map(x => x.expression).concat(...extraFilters)
                    : extraFilters;

                const selectsWithIndexNumber = props.selects.map((x, index) => ({
                    select: x,
                    index: index,
                }));

                const selectGroups = IterUtils.groupBy(selectsWithIndexNumber, x =>
                    x.select.filter != undefined ? JSON.stringify(x.select.filter) : ""
                );

                const queryResultDataByGroup = await Promise.all(
                    Iterator.from(selectGroups.entries())
                        .map(async ([_, selects]) => {
                            const firstSelect = selects[0] ?? reject();
                            const selectFilter = firstSelect.select.filter;
                            const finalFilters =
                                selectFilter != undefined ? baseFilters.concat(selectFilter) : baseFilters;
                            const queryResult = await aggregationStrategy.query(
                                props.multiplicityPath,
                                finalFilters,
                                selects.map(x => x.select),
                                abortSignal
                            );
                            return queryResult.aggregations.map((x, index) => {
                                const originalIndex = selects[index]?.index ?? reject();
                                return {
                                    index: originalIndex,
                                    result: x,
                                };
                            });
                        })
                        .toArray()
                );

                const queryResultData = IterUtils.sortBy(queryResultDataByGroup.flat(), x => x.index).map(
                    x => x.result
                );

                abortSignal.throwIfAborted();
                setResult({
                    isLoading: false,
                    isStale: false,
                    error: undefined,
                    result: queryResultData.map(x => wrapDecimalOrUndefined(x)),
                });
            } catch (e: unknown) {
                if (!isAbortException(e)) {
                    setResult({
                        isLoading: false,
                        isStale: false,
                        error: e instanceof Error ? e : new Error("Unknown Error"),
                        result: props.selects.map(_ => undefined),
                    });
                }
            }
        },
        [
            aggregationStrategy,
            props.multiplicityPath,
            props.selects,
            props.filter,
            props.includeMultilineFilters ? filters : null,
            props.includeMultilineFilters ? isMultilineConnected : null,
            changesVersion,
            isFormReloading,
        ]
    );

    return result;
}
